import { IPartner } from "api/types/_news";
import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownOption from "components/Common/DropdownOption";
import InputsRange from "components/Common/InputsRange";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { ARR_INDEX_TEXT_DAY_OF_WEEK } from "helpers/constans";
import { filterMediaOptions, formatNumberWithCommas } from "helpers/format";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import moment from "moment";
import NestingFilter from "pages/News/Components/NestingFilter";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getAllPartners, approvedArticleStatistics as onGetApprovedArticleStatistics } from "../../../store/thunks";


const SORT_BY_DEFAULT = 'date';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const ApprovedArticleStatistic = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const APPROVAL_ARTICLE_OPTIONS_LANG = [{ value: '1', label: 'Approved' }, { value: '0', label: 'Not Approved' }]?.map((item: any) => ({
        value: item?.value,
        label: t(item?.label),
    }));

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        date_start: withDefault(StringParam, `${moment().subtract(30, 'days').format("Y-MM-DD")}`),
        date_end: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        media_id: withDefault(StringParam, ''),
        impression_sum_start: withDefault(StringParam, ''),
        impression_sum_end: withDefault(StringParam, ''),
        click_sum_start: withDefault(StringParam, ''),
        click_sum_end: withDefault(StringParam, ''),
        ctr_sum_start: withDefault(StringParam, ''),
        ctr_sum_end: withDefault(StringParam, ''),
        approved_article_count_start: withDefault(StringParam, ''),
        approved_article_count_end: withDefault(StringParam, ''),
        naver_article_count_start: withDefault(StringParam, ''),
        naver_article_count_end: withDefault(StringParam, ''),

    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Statistic;

    const StatisticProperties = createSelector(
        selectLayoutState,
        (state) => ({
            approvedArticle: state.approvedArticle,
            isApprovedArticleSuccess: state.isApprovedArticleSuccess,
            isApprovedArticleLoading: state.isApprovedArticleLoading,
            error: state.error,
        })
    );

    const { approvedArticle, isApprovedArticleLoading } = useSelector(StatisticProperties);
    const [isCallAllOptionLoading, setIsCallAllOptionLoading] = useState<boolean>(true);


    const [dateSearch, setDateSearch] = useState<any[]>([query?.date_start ? moment(query?.date_start, 'Y-MM-DD').toDate() : null, query?.date_end ? moment(query?.date_end || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;

    const [startImpressionSearch, setStartImpressionSearch] = useState<string>(query?.impression_sum_start || "");
    const [endImpressionSearch, setEndImpressionSearch] = useState<string>(query?.impression_sum_end || "");

    const [startClickSearch, setStartClickSearch] = useState<string>(query?.click_sum_start || "");
    const [endClickSearch, setEndClickSearch] = useState<string>(query?.click_sum_end || "");

    const [startCtrSearch, setStartCtrSearch] = useState<string>(query?.ctr_sum_start || "");
    const [endCtrSearch, setEndCtrSearch] = useState<string>(query?.ctr_sum_end || "");

    const [startApprovedArticleCountSearch, setStartApprovedArticleCountSearch] = useState<string>(query?.approved_article_count_start || "");
    const [endApprovedArticleCountSearch, setEndApprovedArticleCountSearch] = useState<string>(query?.approved_article_count_end || "");

    const [startNaverArticleCountSearch, setStartNaverArticleCountSearch] = useState<string>(query?.naver_article_count_start || "");
    const [endNaverArticleCountSearch, setEndNaverArticleCountSearch] = useState<string>(query?.naver_article_count_end || "");

    const [approvalArticleSearch, setApprovalArticleSearch] = useState<Option | null>(APPROVAL_ARTICLE_OPTIONS_LANG?.find((item) => String(item?.value) === String(query?.approval_article)) || null);

    const [mediaSearch, setMediaSearch] = useState<Option | null>(!!query?.media_id ? { value: query?.media_id, label: '' } : null);

    const [partners, setPartners] = useState<IPartner[]>([]);

    useEffect(() => {
        dispatch(onGetApprovedArticleStatistics(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        if (isApprovedArticleLoading) {
            return;
        }
        setQuery({
            ...query,
            page: 1,
            date_start: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            date_end: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            media_id: mediaSearch?.value || '',

            approval_article: approvalArticleSearch?.value,

            impression_sum_start: startImpressionSearch || '',
            impression_sum_end: endImpressionSearch || '',

            click_sum_start: startClickSearch || '',
            click_sum_end: endClickSearch || '',

            ctr_sum_start: startCtrSearch || '',
            ctr_sum_end: endCtrSearch || '',

            approved_article_count_start: startApprovedArticleCountSearch || '',
            approved_article_count_end: endApprovedArticleCountSearch || '',

            naver_article_count_start: startNaverArticleCountSearch || '',
            naver_article_count_end: endNaverArticleCountSearch || '',

            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            date_start: `${moment().subtract(30, 'days').format("Y-MM-DD")}`,
            date_end: `${moment().format("Y-MM-DD")}`,
            media_id: '',
            approval_article: '',
            impression_sum_start: '',
            impression_sum_end: '',
            click_sum_start: '',
            click_sum_end: '',
            ctr_sum_start: '',
            ctr_sum_end: '',
            approved_article_count_start: '',
            approved_article_count_end: '',
            naver_article_count_start: '',
            naver_article_count_end: '',
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setDateSearch([moment(queryNew?.date_start || "", 'Y-MM-DD').toDate(), moment(queryNew?.date_end || "", 'Y-MM-DD').toDate()]);
        setMediaSearch({ label: t('All'), value: '' });
        setApprovalArticleSearch({ label: t('All'), value: '' });
        setStartImpressionSearch(queryNew?.impression_sum_start);
        setEndImpressionSearch(queryNew?.impression_sum_end);
        setStartClickSearch(queryNew?.click_sum_start);
        setEndClickSearch(queryNew?.click_sum_end);
        setStartCtrSearch(queryNew?.ctr_sum_start);
        setEndCtrSearch(queryNew?.ctr_sum_end);
        setStartApprovedArticleCountSearch(queryNew?.approved_article_count_start);
        setEndApprovedArticleCountSearch(queryNew?.approved_article_count_end);
        setStartNaverArticleCountSearch(queryNew?.naver_article_count_start);
        setEndNaverArticleCountSearch(queryNew?.naver_article_count_end);
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Date'),
                accessor: "date",
                filterable: true,
                sortable: true,
                thWidth: 150,
                thClass: 'align-middle text-start',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const arrDate = String(cell?.value || '').split(' ');
                    const num = moment(cell?.value).days(); // 0 is Sunday , 1 is Monday, ... , 6 is Saturday
                    const classText = num === 0 ? 'text-danger row-bg-danger' : (num === 6 ? 'text-primary row-bg-primary' : '');
                    const textNote = t(`STANDS_${ARR_INDEX_TEXT_DAY_OF_WEEK[num]}`);
                    return (
                        !!item?.text_row ? <strong>{item?.text_row} </strong> :
                            <div className="text-start" style={{ minWidth: '120px' }}>
                                <span className={classText}>{moment(arrDate?.[0] || '', 'Y-MM-DD').format("MM-DD")} ({textNote})</span>
                            </div>

                    )
                },
            },
            {
                Header: t('Clicks per Approved Article'),
                accessor: "click_per_approved_article",
                filterable: true,
                sortable: true,
                thWidth: i18n?.language === 'en' ? 250 : 150,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '230px' : '120px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Approved Articles'),
                accessor: "approved_article_count",
                filterable: true,
                sortable: true,
                thWidth: i18n?.language === 'en' ? 180 : 140,
                thClass: 'align-middle text-end',
                description: t("Updated hourly"),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-info" style={{ minWidth: i18n?.language === 'en' ? '140px' : '100px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Naver Articles'),
                accessor: "naver_article_count",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 200 : 130,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '140px' : '100px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Impression'),
                accessor: "impression_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 120 : 100,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '100px' : '80px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Click'),
                accessor: "click_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 120 : 100,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: i18n?.language === 'en' ? '100px' : '80px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('CTR'),
                accessor: "ctr_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 120 : 100,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '100px' : '80px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
            {
                Header: '',
                accessor: "___",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 50,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '30px' }}></div>
                        </>
                    )
                },
            },
        ],
        [i18n?.language, userPermissions]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangeDateRangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                setIsCallAllOptionLoading((_prev) => true);
                const [res1]: any = await Promise.all([getAllPartners()]);
                setIsCallAllOptionLoading((_prev) => false);
                setPartners((_prev: any) => res1?.data);
                if (query?.media_id) {
                    const _partner = res1?.data?.find((_p: any) => String(_p?.id) === String(query?.media_id));
                    setMediaSearch({ value: `${_partner?.id}`, label: _partner?.name });
                }

            } catch (error: any) {
                setIsCallAllOptionLoading((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    useEffect(() => {
        document.title = `${t('Approved Article Statistics')} | ${t('PRESS Ai')}`;
        document.body.classList.remove('vertical-sidebar-enable');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Approved Article Statistics')} pageTitle={t('Statistics')} />
                    {isHavePermissionRole(ROLES_FOR_APP.APPROVED_ARTICLE_STATISTICS, userPermissions) && (
                        <Row>
                            <Col sm={12} md={12}>
                                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                    <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                    <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                        <Row className="g-4 align-items-center">
                                            <div className="col-sm">
                                                <div>
                                                    <h5 className="card-title mb-0">
                                                        <span className="me-2">{t('Total')}:</span>
                                                        {(isApprovedArticleLoading) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                                                            start={0}
                                                            end={approvedArticle?.pagination?.total || 0}
                                                            duration={1}
                                                            className="text-primary"
                                                        />)}
                                                    </h5>
                                                </div>
                                            </div>
                                        </Row>
                                    </CardHeader>
                                    <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4 px-0 px-lg-3 pt-0 pt-lg-3">
                                        <NestingFilter>
                                            <Row className="g-4 align-items-center mt-0">
                                                <Col sm={6} md={6} xl={3} xxl={2} className='mt-3 mt-xxl-2'>
                                                    <LabelWrapper label={t('Media')} isShow={true}>
                                                        <DropdownOption
                                                            name="media"
                                                            dataList={filterMediaOptions(partners || [])?.map((_p: any) => ({ label: _p?.name, value: `${_p?.id}` }))}
                                                            placeholder={`${t("Media")}...`}
                                                            className="dropdown-status-rounded z-hight"
                                                            classNamePrefix="name-prefix"
                                                            initialValue={mediaSearch || null}
                                                            onChangeSelect={(e: any) => setMediaSearch(e)}
                                                            isHasOptionAll={true}
                                                            optionAll={{ label: t('All'), value: '' }}
                                                        />
                                                    </LabelWrapper>
                                                </Col>

                                                <Col sm={6} md={6} xl={3} xxl={2} className='date-picker-wrapper-custom mt-3 mt-xxl-2'>
                                                    <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                                        <DatePickerCustom
                                                            placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                            startDate={startDate || null}
                                                            endDate={endDate || null}
                                                            onChangePicker={handleChangeDateRangePicker}
                                                            showOptions={[
                                                                'today', 'yesterday', 'two_day_ago',
                                                                'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                                'this_week', 'last_week',
                                                                'this_month', 'last_month',
                                                            ]}
                                                        />
                                                    </LabelWrapper>
                                                </Col>
                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3 mt-xxl-2' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startApprovedArticleCountSearch}
                                                        valueTo={endApprovedArticleCountSearch}
                                                        placeholderFrom={`${t('Approved Articles (Start)')}...`}
                                                        placeholderTo={`${t('Approved Articles (End)')}...`}
                                                        labelFrom={`${t('Approved Articles (Start)')}`}
                                                        labelTo={`${t('Approved Articles (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartApprovedArticleCountSearch(val)}
                                                        onChangeTo={(val) => setEndApprovedArticleCountSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>

                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3 mt-xxl-2' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startNaverArticleCountSearch}
                                                        valueTo={endNaverArticleCountSearch}
                                                        placeholderFrom={`${t('Naver Articles (Start)')}...`}
                                                        placeholderTo={`${t('Naver Articles (End)')}...`}
                                                        labelFrom={`${t('Naver Articles (Start)')}`}
                                                        labelTo={`${t('Naver Articles (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartNaverArticleCountSearch(val)}
                                                        onChangeTo={(val) => setEndNaverArticleCountSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>

                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3 ' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startImpressionSearch}
                                                        valueTo={endImpressionSearch}
                                                        placeholderFrom={`${t('Impression (Start)')}...`}
                                                        placeholderTo={`${t('Impression (End)')}...`}
                                                        labelFrom={`${t('Impression (Start)')}`}
                                                        labelTo={`${t('Impression (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartImpressionSearch(val)}
                                                        onChangeTo={(val) => setEndImpressionSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startClickSearch}
                                                        valueTo={endClickSearch}
                                                        placeholderFrom={`${t('Click (Start)')}...`}
                                                        placeholderTo={`${t('Click (End)')}...`}
                                                        labelFrom={`${t('Click (Start)')}`}
                                                        labelTo={`${t('Click (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartClickSearch(val)}
                                                        onChangeTo={(val) => setEndClickSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startCtrSearch}
                                                        valueTo={endCtrSearch}
                                                        placeholderFrom={`${t('CTR (Start)')}...`}
                                                        placeholderTo={`${t('CTR (End)')}...`}
                                                        labelFrom={`${t('CTR (Start)')}`}
                                                        labelTo={`${t('CTR (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartCtrSearch(val)}
                                                        onChangeTo={(val) => setEndCtrSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>

                                                <Col sm={6} md={6} xl={12} xxl={12} className="hstack gap-1 justify-content-center justify-content-sm-end justify-content-xl-end mt-3">
                                                    <div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary me-2 "
                                                            onClick={searchData}
                                                            disabled={(isApprovedArticleLoading || isCallAllOptionLoading)}
                                                        >
                                                            {(isApprovedArticleLoading || isCallAllOptionLoading) ? <div className="me-1" style={{ display: 'inline-table', verticalAlign: 'top' }}><Spinner size="sm" style={{ width: '14px', height: '14px' }}></Spinner></div> : <i className="ri-search-line align-bottom me-1"></i>}{" "}
                                                            {t('Button Search')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary fs-14"
                                                            onClick={resetData}
                                                        >
                                                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                            {t('Button Reset')}
                                                        </button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </NestingFilter>
                                    </div>
                                    <CardBody className="pt-3">
                                        <TableContainer
                                            className="custom-header-css"
                                            divClass="table-responsive table-card"
                                            tableClass="align-middle table-bordered-dashed sticky-table-approved-article-statistic table-have-first-row-is-total"
                                            theadClass="table-light text-muted"
                                            columns={columns}
                                            data={approvedArticle?.list?.length ? (
                                                [
                                                    {
                                                        ...approvedArticle?.total,
                                                        text_row: t('Sum')
                                                    },
                                                    ...approvedArticle?.list]
                                            ) : []}
                                            customPageSize={query.limit}
                                            customPageIndex={query.page - 1}
                                            totalRecords={approvedArticle?.pagination?.total}
                                            customPageCount={Math.ceil(Number(approvedArticle?.pagination?.total) / Number(approvedArticle?.pagination?.limit))}
                                            handleChangePage={handleChangePage}
                                            manualSorting={true}
                                            sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                            handleChangeSorting={handleChangeSorting}
                                            isLoading={isApprovedArticleLoading}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ApprovedArticleStatistic;
